import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import {
  AlertTriangle, ArrowRight, BellRing, Clock, Loader2, Mail,
  RefreshCw, MessageSquare, ShieldCheck, ShieldX, Users, X,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter-216 — Admin Onboarding Funnel tile.
 *
 * Surfaces talent IDV pipeline at a glance with:
 *   • 6 bucket cards (not_started, pending_review, pending_over_24h,
 *     pending_over_48h, approved, rejected) + count badges.
 *   • Conversion percentages and average pending-review hours.
 *   • Bulk reminder modal per bucket + per-talent reminder list.
 *   • 12h cooldown badges so we don't spam talent.
 *   • Live refresh on demand.
 */

const BUCKETS = [
  {
    key: 'not_started',
    label: 'Not Started',
    blurb: 'Talent who haven\u2019t uploaded any ID yet',
    tone: 'border-amber-200 bg-amber-50',
    accent: 'text-amber-700',
    badge: 'bg-amber-500 text-white',
    Icon: ShieldCheck,
    canRemind: true,
    deepLink: '/admin/idv-review',
  },
  {
    key: 'pending_review',
    label: 'Pending Review',
    blurb: 'In the review queue right now',
    tone: 'border-cyan-200 bg-cyan-50',
    accent: 'text-cyan-700',
    badge: 'bg-cyan-500 text-white',
    Icon: Clock,
    canRemind: true,
    deepLink: '/admin/idv-review?status=pending',
  },
  {
    key: 'pending_over_24h',
    label: 'Pending > 24h',
    blurb: 'Submitted but waiting over a day',
    tone: 'border-orange-200 bg-orange-50',
    accent: 'text-orange-700',
    badge: 'bg-orange-500 text-white',
    Icon: AlertTriangle,
    canRemind: true,
    deepLink: '/admin/idv-review?status=pending',
  },
  {
    key: 'pending_over_48h',
    label: 'Pending > 48h',
    blurb: 'Critical \u2014 unblock now',
    tone: 'border-rose-200 bg-rose-50',
    accent: 'text-rose-700',
    badge: 'bg-rose-500 text-white',
    Icon: AlertTriangle,
    canRemind: true,
    deepLink: '/admin/idv-review?status=pending',
  },
  {
    key: 'approved',
    label: 'Approved',
    blurb: 'Verified \u2014 can apply to gigs',
    tone: 'border-emerald-200 bg-emerald-50',
    accent: 'text-emerald-700',
    badge: 'bg-emerald-500 text-white',
    Icon: ShieldCheck,
    canRemind: false,
    deepLink: '/admin/idv-review?status=approved',
  },
  {
    key: 'rejected',
    label: 'Rejected',
    blurb: 'Re-upload required',
    tone: 'border-zinc-200 bg-zinc-50',
    accent: 'text-zinc-700',
    badge: 'bg-zinc-700 text-white',
    Icon: ShieldX,
    canRemind: true,
    deepLink: '/admin/idv-review?status=rejected',
  },
];

const formatHoursAgo = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diffH = (Date.now() - d.getTime()) / (1000 * 60 * 60);
    if (diffH < 1) return `${Math.max(1, Math.round(diffH * 60))}m ago`;
    if (diffH < 24) return `${Math.round(diffH)}h ago`;
    return `${Math.round(diffH / 24)}d ago`;
  } catch (_) { return null; }
};

const OnboardingFunnelCard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBucket, setActiveBucket] = useState(null); // open per-talent list drawer for bucket
  const [bulkDialog, setBulkDialog] = useState(null);     // bulk confirm modal for bucket
  const [bulkChannels, setBulkChannels] = useState({ email: true, sms: true });
  const [sending, setSending] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/onboarding-funnel');
      setSummary(r.data);
    } catch (e) {
      toast.error('Failed to load onboarding funnel');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const counts = summary?.counts || {};
  const conv = summary?.conversion || {};
  const avgHrs = summary?.avg_pending_review_hours || 0;
  const cooldownHrs = summary?.cooldown_hours || 12;

  const openBucketList = (bucketKey) => {
    setActiveBucket(bucketKey);
  };

  const openBulkDialog = (bucketKey) => {
    const meta = BUCKETS.find(b => b.key === bucketKey);
    if (!meta?.canRemind) return;
    const count = counts[bucketKey] || 0;
    if (!count) {
      toast.info('No talent in this bucket to remind.');
      return;
    }
    setBulkChannels({ email: true, sms: true });
    setBulkDialog({ bucket: bucketKey, label: meta.label, count });
  };

  const submitBulk = async () => {
    if (!bulkDialog) return;
    const channels = Object.entries(bulkChannels).filter(([, v]) => v).map(([k]) => k);
    if (channels.length === 0) {
      toast.error('Choose at least one channel');
      return;
    }
    setSending(true);
    try {
      const r = await api.post('/admin/onboarding-funnel/remind', {
        bucket: bulkDialog.bucket,
        send_to_bucket: true,
        channels,
      });
      const { attempted, sent, skipped, failed } = r.data;
      toast.success(
        `Reminders sent to ${sent}/${attempted}. ${skipped} skipped (opt-out/cooldown), ${failed} failed.`
      );
      setBulkDialog(null);
      fetchSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  const conversionLine = useMemo(() => ([
    { label: 'Submission rate', value: conv.submission_rate_pct, hint: 'started \u2192 submitted' },
    { label: 'Approval rate', value: conv.approval_rate_pct, hint: 'of decisions' },
    { label: 'Gig-ready', value: conv.gig_ready_rate_pct, hint: 'total \u2192 approved' },
  ]), [conv]);

  return (
    <>
      <Card
        className="bg-white border-gray-200 shadow-sm mb-6 overflow-hidden"
        data-testid="onboarding-funnel-card"
      >
        <CardContent className="p-4 sm:p-5">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-600" />
                Onboarding Funnel
              </h2>
              <p className="text-xs text-gray-500">
                Talent IDV pipeline · {counts.total_talent || 0} active talent
                {counts.stalled_not_started > 0 && (
                  <span className="ml-2 text-rose-600 font-medium">
                    · {counts.stalled_not_started} stalled &gt;7d
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm" onClick={fetchSummary}
                disabled={loading}
                data-testid="onboarding-funnel-refresh"
              >
                {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Refresh
              </Button>
            </div>
          </div>

          {/* Conversion + avg pending */}
          <div
            className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4"
            data-testid="onboarding-funnel-metrics"
          >
            {conversionLine.map(c => (
              <div
                key={c.label}
                className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
              >
                <p className="text-[11px] uppercase tracking-wide text-gray-500">{c.label}</p>
                <p className="text-base font-semibold text-gray-900">
                  {Number.isFinite(c.value) ? `${c.value}%` : '\u2014'}
                </p>
                <p className="text-[11px] text-gray-500 leading-tight">{c.hint}</p>
              </div>
            ))}
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-gray-500">Avg pending</p>
              <p className="text-base font-semibold text-gray-900">{avgHrs}h</p>
              <p className="text-[11px] text-gray-500 leading-tight">time-in-queue</p>
            </div>
          </div>

          {/* Bucket grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {BUCKETS.map(b => {
              const n = counts[b.key] ?? 0;
              return (
                <div
                  key={b.key}
                  className={`relative rounded-lg border ${b.tone} p-3 transition-shadow hover:shadow-sm`}
                  data-testid={`onboarding-funnel-bucket-${b.key}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <b.Icon className={`w-5 h-5 ${b.accent}`} />
                    <Badge
                      className={`${b.badge} hover:${b.badge}`}
                      data-testid={`onboarding-funnel-bucket-${b.key}-count`}
                    >
                      {n}
                    </Badge>
                  </div>
                  <p className="font-semibold text-gray-900 text-sm">{b.label}</p>
                  <p className="text-xs text-gray-600 mt-0.5 leading-tight">{b.blurb}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <Button
                      size="sm" variant="outline"
                      className="h-7 text-xs px-2 bg-white"
                      onClick={() => openBucketList(b.key)}
                      data-testid={`onboarding-funnel-bucket-${b.key}-view`}
                    >
                      View list <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                    {b.canRemind && n > 0 && (
                      <Button
                        size="sm"
                        className="h-7 text-xs px-2 bg-cyan-600 hover:bg-cyan-700 text-white"
                        onClick={() => openBulkDialog(b.key)}
                        data-testid={`onboarding-funnel-bucket-${b.key}-bulk-remind`}
                      >
                        <BellRing className="w-3 h-3 mr-1" /> Remind all
                      </Button>
                    )}
                    {b.deepLink && (
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 text-xs px-2 text-gray-700"
                        onClick={() => navigate(b.deepLink)}
                        data-testid={`onboarding-funnel-bucket-${b.key}-deeplink`}
                      >
                        Open queue
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cooldown explainer */}
          <p className="text-[11px] text-gray-500 mt-3">
            Reminders respect a {cooldownHrs}h cooldown per channel and talent opt-out preferences. Failed sends are logged.
          </p>
        </CardContent>
      </Card>

      {/* Bulk confirm modal */}
      <Dialog open={!!bulkDialog} onOpenChange={(o) => !o && setBulkDialog(null)}>
        <DialogContent className="max-w-md" data-testid="onboarding-funnel-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Send bulk reminder</DialogTitle>
            <DialogDescription>
              {bulkDialog && (
                <>
                  This will attempt to send a reminder to{' '}
                  <span className="font-semibold text-gray-900">{bulkDialog.count}</span> talent in the{' '}
                  <span className="font-semibold text-gray-900">{bulkDialog.label}</span> bucket. Talent within the {cooldownHrs}h cooldown or with the channel opted-out will be skipped.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm font-medium text-gray-900">Channels</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={bulkChannels.email}
                onCheckedChange={(v) => setBulkChannels(s => ({ ...s, email: !!v }))}
                data-testid="onboarding-funnel-bulk-channel-email"
              />
              <Mail className="w-4 h-4 text-gray-600" />
              <span className="text-sm">Email</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={bulkChannels.sms}
                onCheckedChange={(v) => setBulkChannels(s => ({ ...s, sms: !!v }))}
                data-testid="onboarding-funnel-bulk-channel-sms"
              />
              <MessageSquare className="w-4 h-4 text-gray-600" />
              <span className="text-sm">SMS</span>
            </label>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline" onClick={() => setBulkDialog(null)} disabled={sending}
              data-testid="onboarding-funnel-bulk-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitBulk} disabled={sending}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="onboarding-funnel-bulk-confirm"
            >
              {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <BellRing className="w-3 h-3 mr-1" />}
              Send reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-talent drawer */}
      {activeBucket && (
        <BucketListDialog
          bucketKey={activeBucket}
          onClose={() => { setActiveBucket(null); fetchSummary(); }}
          cooldownHours={cooldownHrs}
        />
      )}
    </>
  );
};


const BucketListDialog = ({ bucketKey, onClose, cooldownHours }) => {
  const meta = BUCKETS.find(b => b.key === bucketKey);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/onboarding-funnel/list/${bucketKey}?limit=100`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error('Failed to load talent list');
    } finally {
      setLoading(false);
    }
  }, [bucketKey]);

  useEffect(() => { load(); }, [load]);

  const remindOne = async (item, channels) => {
    setBusyId(item.user_id);
    try {
      const r = await api.post('/admin/onboarding-funnel/remind', {
        bucket: bucketKey,
        talent_user_ids: [item.user_id],
        channels,
      });
      const { sent, skipped, failed } = r.data;
      if (sent) toast.success(`Reminder sent to ${item.name}`);
      else if (skipped) toast.info(`Skipped (cooldown or opt-out): ${item.name}`);
      else if (failed) toast.error(`Failed to remind ${item.name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="onboarding-funnel-bucket-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {meta?.Icon ? <meta.Icon className={`w-5 h-5 ${meta.accent}`} /> : null}
            {meta?.label || bucketKey}
          </DialogTitle>
          <DialogDescription>
            {items.length} talent in this bucket. Reminders respect a {cooldownHours}h cooldown.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-gray-500" data-testid="onboarding-funnel-bucket-empty">
            No talent in this bucket.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100" data-testid="onboarding-funnel-bucket-list">
            {items.map(item => (
              <li
                key={item.user_id}
                className="py-3 flex flex-wrap items-start gap-3"
                data-testid={`onboarding-funnel-row-${item.user_id}`}
              >
                <img
                  src={item.profile_photo || '/avatar.png'}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover border border-gray-200"
                  onError={(e) => { e.currentTarget.src = '/avatar.png'; }}
                />
                <div className="flex-1 min-w-[180px]">
                  <p className="font-medium text-gray-900 text-sm">{item.name}</p>
                  <p className="text-xs text-gray-500">{item.email}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {item.idv_submitted_at && (
                      <span className="text-[11px] text-gray-500">
                        Submitted {formatHoursAgo(item.idv_submitted_at)}
                      </span>
                    )}
                    {item.last_reminder_at && (
                      <span className="text-[11px] text-gray-600">
                        · Last reminder {formatHoursAgo(item.last_reminder_at)}
                        {item.last_reminder_channel ? ` (${item.last_reminder_channel})` : ''}
                      </span>
                    )}
                    {item.in_cooldown && (
                      <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">
                        cooldown
                      </Badge>
                    )}
                    {!item.notify_email && (
                      <Badge variant="outline" className="text-[10px] text-gray-600">
                        email opt-out
                      </Badge>
                    )}
                    {!item.notify_sms && (
                      <Badge variant="outline" className="text-[10px] text-gray-600">
                        sms opt-out
                      </Badge>
                    )}
                    {!item.phone_number && (
                      <Badge variant="outline" className="text-[10px] text-gray-600">
                        no phone
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Button
                    size="sm" variant="outline" className="h-7 text-xs"
                    disabled={busyId === item.user_id || !item.notify_email}
                    onClick={() => remindOne(item, ['email'])}
                    data-testid={`onboarding-funnel-row-${item.user_id}-email`}
                  >
                    <Mail className="w-3 h-3 mr-1" /> Email
                  </Button>
                  <Button
                    size="sm" variant="outline" className="h-7 text-xs"
                    disabled={
                      busyId === item.user_id || !item.notify_sms || !item.phone_number
                    }
                    onClick={() => remindOne(item, ['sms'])}
                    data-testid={`onboarding-funnel-row-${item.user_id}-sms`}
                  >
                    <MessageSquare className="w-3 h-3 mr-1" /> SMS
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs bg-cyan-600 hover:bg-cyan-700 text-white"
                    disabled={busyId === item.user_id}
                    onClick={() => remindOne(item, ['email', 'sms'])}
                    data-testid={`onboarding-funnel-row-${item.user_id}-both`}
                  >
                    {busyId === item.user_id
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <><BellRing className="w-3 h-3 mr-1" /> Both</>}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <DialogFooter>
          <Button
            variant="outline" onClick={onClose}
            data-testid="onboarding-funnel-bucket-close"
          >
            <X className="w-3 h-3 mr-1" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingFunnelCard;
